import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Text, Group, Alert, Center, Loader, Badge, Paper, Stack, TextInput, Select, Radio,
  MultiSelect, Title, UnstyledButton, ScrollArea, Grid,
} from '@mantine/core';
import { IconArrowRight, IconArrowLeft, IconPlus, IconTrash, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import useSchool from '../../../hooks/useSchool';
import MusicUpload from '../../../components/MusicUpload/MusicUpload';
import { postApi } from '../../../lib/api';
import { suggestedFootwear } from '../../../config/categories';
import * as ensembleRules from '../../../shared/ensembleRules';

const inputStyles = { label: { textAlign: 'left' }, description: { textAlign: 'left' } };

const ENSEMBLE_LENGTHS = ['2 minutes', '2.5 minutes', '3 minutes', '3.5 minutes', '4 minutes', '4.5 minutes', '5 minutes']
  .map(v => ({ value: v, label: v }));

const blankDraft = () => ({
  id: null, name: '', piece_title: '', choreographed_by: '', start_music: '', length: '',
  music_file_url: '', music_file_name: '', members: [],
});

const draftFrom = (e) => ({
  id: e.id,
  name: e.name || '',
  piece_title: e.piece_title || '',
  choreographed_by: e.choreographed_by || '',
  start_music: e.start_music || '',
  length: e.length || '',
  music_file_url: e.music_file_url || '',
  music_file_name: e.music_file_name || '',
  members: e.members.map(m => ({ registrationId: m.registrationId, footwear: m.footwear || '' })),
});

const statusLine = (check) => (check?.eligible
  ? { ok: true, text: `${check.division} · avg ${check.averageAge}` }
  : { ok: false, text: check?.problems?.length ? `${check.problems.length} thing${check.problems.length === 1 ? '' : 's'} to fix` : 'Not checked yet' });

/**
 * School portal step 3: ensembles. Teams are built from the roster (a student
 * can be in up to 2) and saved through /api/ensemble (school actions) — the
 * browser can't write the ensemble tables. The rules check runs live with the
 * same function the server uses.
 */
const SchoolEnsembles = ({ readOnly: readOnlyProp = false }) => {
  const navigate = useNavigate();
  const { school, currentSeason } = useSchool();
  const seasonId = currentSeason?.id;
  const year = currentSeason?.competition_year;

  const [data, setData] = useState(null); // { roster, ensembles, finalized }
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null); // ensemble id, 'new', or null
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // After a save: { ok } — a team that breaks the rules still saves, but
  // can't be finalized, so say so next to the button.
  const [savedNote, setSavedNote] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const readOnly = readOnlyProp || Boolean(data?.finalized);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const call = useCallback(async (action, body = {}) => {
    const res = await postApi('/api/ensemble', { action, schoolSeasonId: seasonId, ...body });
    setData(res);
    return res;
  }, [seasonId]);

  useEffect(() => {
    if (!seasonId) return;
    call('schoolStatus').catch(err => setLoadError(err.message));
  }, [seasonId, call]);

  const rosterById = useMemo(
    () => Object.fromEntries((data?.roster || []).map(s => [s.registrationId, s])),
    [data]
  );

  const open = (id) => {
    if (dirty && !window.confirm('Discard your unsaved changes to this ensemble?')) return;
    setError(null);
    setSavedNote(null);
    setDirty(false);
    setSelected(id);
    setDraft(id === 'new' ? blankDraft() : draftFrom(data.ensembles.find(e => e.id === id)));
  };

  const change = (patch) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); setSavedNote(null); };

  const setMembers = (ids) => {
    change({
      members: ids.map(id => draft.members.find(m => m.registrationId === id)
        || { registrationId: id, footwear: suggestedFootwear(rosterById[id]?.dateOfBirth, year) }),
    });
  };

  const setFootwear = (id, footwear) =>
    change({ members: draft.members.map(m => (m.registrationId === id ? { ...m, footwear } : m)) });

  // Live check on the unsaved draft, with the same rules the server applies.
  const liveCheck = useMemo(() => {
    if (!draft) return null;
    const dancers = draft.members
      .map(m => ({ ...rosterById[m.registrationId], footwear: m.footwear }))
      .filter(m => m.dateOfBirth);
    return dancers.length ? ensembleRules.checkEnsemble(dancers, year) : null;
  }, [draft, rosterById, year]);

  const save = async () => {
    setError(null);
    if (!draft.name.trim()) return setError('Please give the ensemble a name.');
    const missing = draft.members.filter(m => !m.footwear).map(m => rosterById[m.registrationId]?.name);
    if (missing.length) return setError(`Choose pointe or soft shoes for: ${missing.join(', ')}.`);
    setSaving(true);
    try {
      const { id, ...fields } = draft;
      const res = await call('schoolSave', { ensembleId: id || undefined, ...fields });
      const savedId = id || res.savedEnsembleId;
      const saved = res.ensembles.find(e => e.id === savedId);
      setDirty(false);
      setSelected(savedId);
      setDraft(draftFrom(saved));
      setSavedNote({ ok: Boolean(saved?.check?.eligible) });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the ensemble "${draft.name || 'Untitled'}"? The students stay on your roster.`)) return;
    setSaving(true);
    try {
      await call('schoolDelete', { ensembleId: draft.id });
      setSelected(null);
      setDraft(null);
      setDirty(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const leave = (to) => {
    if (dirty && !window.confirm('You have unsaved changes to this ensemble. Leave without saving?')) return;
    navigate(to);
  };

  if (loadError) {
    return <Alert color="red" m="xl">{loadError}</Alert>;
  }
  if (!data) {
    return <Center style={{ minHeight: '40vh' }}><Loader color="var(--primary-gold)" /></Center>;
  }

  const list = (
    <div style={{ width: isMobile ? '100%' : 300, borderRight: isMobile ? 'none' : '1px solid #eee', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" p="md" style={{ borderBottom: '1px solid #eee' }}>
        <Text fw={600} style={{ color: 'var(--charcoal)' }}>Ensembles</Text>
        {!readOnly && (
          <Button size="xs" variant="light" color="gold" leftSection={<IconPlus size={14} />} onClick={() => { open('new'); }}>
            New
          </Button>
        )}
      </Group>
      <ScrollArea style={{ flex: 1 }}>
        {data.ensembles.length === 0 && (
          <Text size="sm" color="dimmed" p="md" style={{ textAlign: 'left' }}>
            No ensembles yet. Groups of 3–8 students from your roster; a student can be in up to 2.
          </Text>
        )}
        {data.ensembles.map(e => {
          const st = statusLine(e.check);
          return (
            <UnstyledButton
              key={e.id}
              onClick={() => open(e.id)}
              style={{
                display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
                background: selected === e.id ? 'var(--soft-cream)' : 'transparent',
                borderLeft: selected === e.id ? '3px solid var(--primary-gold)' : '3px solid transparent',
              }}
            >
              <Text size="sm" fw={600} style={{ color: 'var(--charcoal)' }}>{e.name}</Text>
              <Group gap={6} mt={2}>
                {st.ok ? <IconCheck size={13} color="#2b8a3e" /> : <IconAlertTriangle size={13} color="#e8590c" />}
                <Text size="xs" color="dimmed">{e.members.length} dancer{e.members.length === 1 ? '' : 's'} · {st.text}</Text>
              </Group>
            </UnstyledButton>
          );
        })}
      </ScrollArea>
    </div>
  );

  // Pointe / Soft as two plain buttons: nothing looks selected until it is.
  const footwearChoice = (m) => (
    <Group gap={6} wrap="nowrap">
      {!m.footwear && <Text size="xs" c="red">choose</Text>}
      {[['pointe', 'Pointe'], ['soft', 'Soft']].map(([value, label]) => (
        <Button
          key={value}
          size="compact-sm"
          variant={m.footwear === value ? 'filled' : 'default'}
          color="gold"
          onClick={() => setFootwear(m.registrationId, value)}
          disabled={readOnly}
        >
          {label}
        </Button>
      ))}
    </Group>
  );

  const sectionTitle = (text) => (
    <Title order={6} mb="md" style={{ color: 'var(--soft-gray)', textAlign: 'left' }}>{text}</Title>
  );

  const editor = draft && (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, background: 'white' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Stack gap="lg" style={{ maxWidth: 880, margin: '0 auto', padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
          {isMobile && (
            <Button variant="subtle" size="xs" color="gray" leftSection={<IconArrowLeft size={14} />}
              onClick={() => { if (!dirty || window.confirm('Discard unsaved changes?')) { setSelected(null); setDraft(null); setDirty(false); } }}
              style={{ alignSelf: 'flex-start' }}>
              Back to ensembles
            </Button>
          )}

          <Group justify="space-between" align="center" wrap="wrap" gap="xs">
            <Title order={4} style={{ color: 'var(--charcoal)', textAlign: 'left', margin: 0 }}>
              {draft.id ? draft.name || 'Ensemble' : 'New ensemble'}
            </Title>
            {liveCheck && (liveCheck.problems.length === 0
              ? <Badge color="green" variant="light" size="lg" style={{ textTransform: 'none' }}>✓ {liveCheck.division}</Badge>
              : <Badge color="orange" variant="light" size="lg" style={{ textTransform: 'none' }}>
                  {liveCheck.problems.length} thing{liveCheck.problems.length === 1 ? '' : 's'} to fix
                </Badge>)}
          </Group>

          {error && <Alert color="red" p="xs"><Text size="sm" style={{ textAlign: 'left' }}>{error}</Text></Alert>}

          {/* Team: name, dancers, footwear */}
          <Paper p="md" radius="md" withBorder>
            {sectionTitle('Team')}
            <Stack gap="md">
              <TextInput label="Ensemble name" required placeholder="e.g. Junior Garland" value={draft.name}
                onChange={(e) => change({ name: e.target.value })} styles={inputStyles} disabled={readOnly} />

              <MultiSelect
                label="Dancers"
                description="3–8 students from your roster. Age shown is the age on January 1."
                placeholder={draft.members.length ? '' : 'Search your roster'}
                searchable
                clearable={!readOnly}
                maxValues={ensembleRules.MAX_DANCERS}
                data={data.roster.map(st => ({ value: st.registrationId, label: `${st.name}${st.age != null ? ` · ${st.age}` : ''}` }))}
                value={draft.members.map(m => m.registrationId)}
                onChange={setMembers}
                styles={inputStyles}
                disabled={readOnly}
                nothingFoundMessage="No student with that name on your roster"
              />

              {draft.members.length > 0 && (
                <div>
                  <Text size="sm" fw={500} style={{ textAlign: 'left' }}>Footwear</Text>
                  <Text size="xs" color="dimmed" mb="xs" style={{ textAlign: 'left' }}>
                    Filled in from age: soft shoes under 12, pointe from 12. Adults choose.
                  </Text>
                  <Stack gap={0}>
                    {draft.members.map((m, i) => {
                      const st = rosterById[m.registrationId];
                      return (
                        <Group key={m.registrationId} justify="space-between" wrap="nowrap" gap="sm"
                          style={{ padding: '8px 0', borderTop: i ? '1px solid #f1f1f1' : 'none' }}>
                          <Text size="sm" style={{ textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere' }}>
                            {st?.name}
                            {st?.age != null && <Text component="span" size="xs" color="dimmed"> · {st.age}</Text>}
                          </Text>
                          {footwearChoice(m)}
                        </Group>
                      );
                    })}
                  </Stack>
                </div>
              )}

              {liveCheck && liveCheck.problems.length > 0 && (
                <Alert color="red" variant="light" p="xs">
                  {liveCheck.problems.map(p => <Text key={p} size="sm" style={{ textAlign: 'left' }}>• {p}</Text>)}
                </Alert>
              )}
              {liveCheck && liveCheck.problems.length === 0 && (
                <Text size="sm" style={{ textAlign: 'left', color: '#2b8a3e' }}>
                  ✓ {liveCheck.division} · average age {liveCheck.averageAge} · {liveCheck.size} dancers
                </Text>
              )}
              {liveCheck?.warnings.map(w => (
                <Alert key={w} color="yellow" variant="light" p="xs">
                  <Text size="sm" style={{ textAlign: 'left' }}>{w}</Text>
                </Alert>
              ))}
            </Stack>
          </Paper>

          {/* Piece details */}
          <Paper p="md" radius="md" withBorder>
            {sectionTitle('Piece details')}
            <Text size="xs" color="dimmed" mt={-8} mb="md" style={{ textAlign: 'left' }}>
              Can be added later, until registration closes.
            </Text>
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Piece title" placeholder="e.g. Garland Waltz" value={draft.piece_title}
                  onChange={(e) => change({ piece_title: e.target.value })} styles={inputStyles} disabled={readOnly} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Choreographer / Coach" placeholder="Teacher's name" value={draft.choreographed_by}
                  onChange={(e) => change({ choreographed_by: e.target.value })} styles={inputStyles} disabled={readOnly} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select label="Length" placeholder="Select length" data={ENSEMBLE_LENGTHS} value={draft.length || null}
                  description="3–5 dancers: up to 3 min · 6–8 dancers: up to 4 min"
                  onChange={(v) => change({ length: v || '' })} styles={inputStyles} disabled={readOnly} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Radio.Group label="Start music" description="Dancers ON or OFF stage when the music begins?"
                  value={draft.start_music} onChange={(v) => change({ start_music: v })} styles={inputStyles}>
                  <Group mt="xs" gap="xl">
                    <Radio value="on_stage" label="ON Stage" disabled={readOnly} />
                    <Radio value="off_stage" label="OFF Stage" disabled={readOnly} />
                  </Group>
                </Radio.Group>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" fw={500} mb={4} style={{ textAlign: 'left' }}>Music</Text>
                {draft.members.length ? (
                  <MusicUpload
                    // Stored in the first dancer's folder (the teacher owns every
                    // student's folder); the API checks it's on this roster.
                    registrationId={draft.members[0].registrationId}
                    variationNumber={`ensemble_${draft.id || 'new'}`}
                    existingMusicUrl={draft.music_file_url}
                    existingMusicFileName={draft.music_file_name}
                    maxDurationSeconds={300}
                    onUploadComplete={({ url, fileName }) => change({ music_file_url: url, music_file_name: fileName })}
                    onUploadError={(err) => setError(err?.message || 'Music upload failed')}
                  />
                ) : (
                  <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>Pick at least one dancer to upload music.</Text>
                )}
              </Grid.Col>
            </Grid>
          </Paper>
        </Stack>
      </div>

      {/* Actions stay in view, however long the form */}
      {!readOnly && (
        <div style={{ borderTop: '1px solid #eee', background: 'white' }}>
          <Group justify="space-between" wrap="nowrap" gap="sm"
            style={{ maxWidth: 880, margin: '0 auto', padding: isMobile ? '0.75rem 1rem' : '0.75rem 2rem' }}>
            {draft.id ? (
              <Button variant="subtle" color="red" size="sm" leftSection={<IconTrash size={16} />} onClick={remove} disabled={saving}>
                {isMobile ? 'Delete' : 'Delete ensemble'}
              </Button>
            ) : <span />}
            <Group gap="sm" wrap="nowrap">
              {savedNote && !savedNote.ok && (
                <Text size="xs" style={{ color: '#e8590c', textAlign: 'right', maxWidth: isMobile ? 150 : 280 }}>
                  Saved. This team doesn't meet the rules yet — fix it before you finalize.
                </Text>
              )}
              <Button color="gold" onClick={save} loading={saving} disabled={!dirty && Boolean(draft.id)}>
                {draft.id ? (dirty ? 'Save changes' : 'Saved') : 'Create ensemble'}
              </Button>
            </Group>
          </Group>
        </div>
      )}
    </div>
  );

  return (
    // Same height as the Students step, so the list and editor scroll on
    // their own and the Save bar stays pinned at the bottom.
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      {/* Header bar, same as the Students step */}
      <div style={{
        padding: '0.75rem 1.5rem', borderBottom: '1px solid #eee', background: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem',
      }}>
        <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={16} />} size="sm"
          onClick={() => leave('/register/school/students')}>
          Students
        </Button>
        <div style={{ textAlign: 'center' }}>
          <Text fw={700} size="lg" color="var(--charcoal)">{school?.name}</Text>
          <Text size="xs" color="dimmed">Ensembles</Text>
        </div>
        <Group>
          <Badge color="gold" variant="light" size="lg">
            {data.ensembles.length} ensemble{data.ensembles.length !== 1 ? 's' : ''}
          </Badge>
          <Button rightSection={<IconArrowRight size={16} />} size="sm"
            style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
            onClick={() => leave('/register/school/confirmation')}>
            {readOnly ? 'View Summary' : 'Review & Finalize'}
          </Button>
        </Group>
      </div>

      {data.finalized && !readOnlyProp && (
        <Alert color="blue" variant="light" radius={0}>
          <Text size="sm" style={{ textAlign: 'left' }}>
            Your roster is finalized, so ensembles are view-only. To make changes, un-finalize it on the review step.
          </Text>
        </Alert>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {isMobile ? (draft ? editor : list) : (
          <>
            {list}
            {editor || (
              <Center style={{ flex: 1, padding: '2rem' }}>
                <Stack align="center" gap="sm" maw={420}>
                  <Text color="dimmed" ta="center">
                    {data.ensembles.length
                      ? 'Select an ensemble to view or edit it.'
                      : 'Entering any group pieces this year? Create an ensemble from your roster.'}
                  </Text>
                  {!readOnly && (
                    <Button variant="light" color="gold" leftSection={<IconPlus size={16} />} onClick={() => open('new')}>
                      New ensemble
                    </Button>
                  )}
                  {!data.ensembles.length && (
                    <Button variant="subtle" color="gray" size="xs" onClick={() => navigate('/register/school/confirmation')}>
                      No ensembles this year? Continue to review
                    </Button>
                  )}
                </Stack>
              </Center>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolEnsembles;
